import { useState, useEffect } from "react";
import axios from "axios";
import WinnerTable from "../components/winnerTable";
import Pagination from "../components/newpagination";
import Layout from "../containers/layout";
import Cookies from "js-cookie";
import * as XLSX from "xlsx";

import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Spinner,
  Box,
  Grid,
  Toast,
  Modal,
} from "@shopify/polaris";

const Winners = () => {
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState({
    from: "",
    till: "",
  });
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const perPage = 50;
  const token = Cookies.get("token");
  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  const showNotification = (tone, title) => {
    setNotification({ tone, title });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  const fetchWinners = async () => {
    try {
      setLoading(true);

      const res = await axios.get("https://api.iplanet.one/bdaysale/winners", {
        headers: { Authorization: token },
      });

      if (res.data.success) {
        const apiData = res.data.winners;

        let flattened = [];
        const seenUserKeys = new Set(); // duplicate users track karne ke liye

        apiData.forEach((entry) => {
          // ✅ Document ka createdAt use karo, user ka nahi
          const createdDate = formatDate(entry.createdAt);

          Object.keys(entry.winners).forEach((state) => {
            const users = entry.winners[state];

            if (users.length > 0) {
              users.forEach((user) => {
                // ✅ Same _id + same date + same state = duplicate, skip karo
                const uniqueKey = `${user._id}-${createdDate}-${state}`;

                if (!seenUserKeys.has(uniqueKey)) {
                  seenUserKeys.add(uniqueKey);
                  flattened.push({
                    ...user,
                    state,
                    date: createdDate,
                  });
                }
              });
            } else {
              // Blank row sirf agar is date+state mein koi real user nahi
              const hasRealUser = flattened.some(
                (f) => f.date === createdDate && f.state === state && !f.empty,
              );
              const hasBlankRow = flattened.some(
                (f) => f.date === createdDate && f.state === state && f.empty,
              );

              if (!hasRealUser && !hasBlankRow) {
                flattened.push({
                  state,
                  date: createdDate,
                  name: "--",
                  phone: "--",
                  email: "--",
                  empty: true,
                });
              }
            }
          });
        });
        const finalFlattened = flattened.filter((item) => {
          if (!item.empty) return true;
          return !flattened.some(
            (f) => !f.empty && f.date === item.date && f.state === item.state,
          );
        });

        setProducts(finalFlattened);
        setTotalPages(Math.ceil(finalFlattened.length / perPage));
      }
    } catch (error) {
      showNotification("critical", "API failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWinners();
  }, []);

  const handleAnnounce = (item) => {
    setSelectedWinner(item);
    setConfirmModal(true);
  };

  const confirmAnnounceWinner = async () => {
    try {
      setLoading(true);

      const payload = {
        id: selectedWinner._id,
      };

      const res = await axios.post(
        "https://api.iplanet.one/bdaysale/winners/select",
        payload,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (res.data.success) {
        showNotification(
          "success",
          res.data.message || "Winner announced successfully",
        );

        fetchWinners();
      } else {
        showNotification(
          "critical",
          res.data.message || "Something went wrong",
        );
      }
    } catch (error) {
      showNotification("critical", "API failed");
    } finally {
      setLoading(false);
      setConfirmModal(false);
      setSelectedWinner(null);
    }
  };

  const handleDownload = () => {
    try {
      let sheetData = [];

      sheetData.push(["Date", "State", "User", "Phone", "Prize", "Status"]);

      products.forEach((item) => {
        sheetData.push([
          item.date,
          item.state,
          item.name || "--",
          item.phone || "--",
          item.prize || "--",
          item.empty
            ? "No Data"
            : item.alreadyWinner
              ? "Winner"
              : "Winner Already Announced",
        ]);
      });
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      worksheet["!cols"] = [
        { wch: 18 },
        { wch: 12 },
        { wch: 30 },
        { wch: 20 },
        { wch: 30 },
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Winners");

      XLSX.writeFile(workbook, "winners.xlsx");

      showNotification("success", "Exported successfully");
    } catch (error) {
      showNotification("critical", "Export failed");
    }
  };

  return (
    <Layout>
      <Page fullWidth>
        <BlockStack gap="500">
          {notification && (
            <Toast
              content={notification.title}
              tone={notification.tone}
              onDismiss={() => setNotification(null)}
            />
          )}

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingLg">Winners</Text>

                <Button variant="primary" onClick={handleDownload}>
                  Export
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            {loading ? (
              <div
                style={{
                  padding: "120px 0",
                  textAlign: "center",
                }}
              >
                <Spinner size="large" />
              </div>
            ) : (
              <WinnerTable
                products={products}
                indexing={currentPage}
                perPage={perPage}
                onAnnounce={handleAnnounce}
              />
            )}
          </Card>

          <Modal
            open={confirmModal}
            onClose={() => {
              setConfirmModal(false);
              setSelectedWinner(null);
            }}
            title="Confirm Winner Announcement"
            primaryAction={{
              content: "Confirm",
              destructive: true,
              loading: loading,
              onAction: confirmAnnounceWinner,
            }}
            secondaryActions={[
              {
                content: "Cancel",
                onAction: () => {
                  setConfirmModal(false);
                  setSelectedWinner(null);
                },
              },
            ]}
          >
            <Modal.Section>
              <Text>
                Are you sure you want to announce <b>{selectedWinner?.name}</b>{" "}
                as winner for <b>{selectedWinner?.state}</b> ?
              </Text>
            </Modal.Section>
          </Modal>
        </BlockStack>
      </Page>
    </Layout>
  );
};

export default Winners;
